import { ARENA_DEFAULT_ROUND_TIME_S, LADDERS, MAPS, MAX_ROOM_CAP, MODIFIERS } from "@neon-purge/shared";
import { el } from "./dom.js";
import { showLoading, hideLoading } from "./loadingOverlay.js";
import { createRoom, createSoloRoom, joinByCode, joinById, joinPublicRoom, joinQuickPlay, listRooms } from "../net/game.js";
import { audio } from "../game/audio.js";
import { createMapPreview, type MapPreviewHandle } from "./mapPreview.js";
import { mapGrid, type MapGridHandle } from "./mapGrid.js";
import { api } from "../net/api.js";
import { DIFFICULTIES, MODE_IDS, MODE_ORDER, PICKUP_DEMOTE_LABEL, TEAM_COLOR, fillRoundSelect, hasPickupDemoteOption, modeDesc, modeVoice, teamColor } from "./modeInfo.js";
import { padRumble } from "../game/input.js";
import { isTouchDevice } from "../game/touch.js";
import type { Room } from "colyseus.js";

/** PHONE: dense desktop sections collapse into tap-to-open drawers — the
 *  common flow (map, mode, bots, PLAY) stays one screen with big targets.
 *  On desktop this is a plain pass-through wrapper. */
function fold(title: string, ...kids: (HTMLElement | Text)[]): HTMLElement {
  if (!isTouchDevice()) return el("div", {}, ...(kids as HTMLElement[]));
  const d = document.createElement("details");
  d.className = "mob-fold";
  const s = document.createElement("summary");
  s.textContent = title;
  d.append(s, ...kids);
  return d;
}

export interface PlayActions {
  onJoined: (room: Room) => void;
  onBack: () => void;
  /** Open the MULTIPLAYER LOCAL (split-screen) lobby. */
  onLocal: () => void;
}

/** Lobby: sectioned cards — Map (3D preview), Mode, AI, Rooms. */
export function playScreen(actions: PlayActions): HTMLElement {
  const screen = el("div", { class: "screen play-screen" });
  const errorEl = el("div", { class: "error" });

  let preview: MapPreviewHandle | null = null;
  const cleanup = (): void => { preview?.dispose(); preview = null; };
  const busy = (b: boolean): void => screen.querySelectorAll("button").forEach((btn) => (btn.disabled = b));
  const join = (p: Promise<Room>): void => {
    busy(true);
    errorEl.textContent = "";
    // instant feedback: reveal the launch loader the moment PLAY is clicked, so
    // the connect round-trip + scene build reads as loading, not a frozen menu.
    // MatchMode drives it through the build; a lobby/drop-in landing hides it.
    showLoading("CONNECTING");
    // Capture the pointer NOW, while this click's transient activation is
    // still valid — map loading often outlives Chrome's ~5s activation
    // window, after which the match's own lock request is silently denied
    // ("I don't have the aim on my mouse when the game starts").
    try { void (document.getElementById("game-canvas") as HTMLCanvasElement | null)?.requestPointerLock(); } catch { /* best-effort */ }
    p.then((room) => { cleanup(); actions.onJoined(room); }).catch((err) => {
      hideLoading(); // join failed — back to an interactive menu, no loader
      if (document.pointerLockElement) document.exitPointerLock(); // cursor back for the error
      errorEl.textContent = (err as Error).message || "join failed";
      busy(false);
    });
  };

  // ---- MAP card: 3D preview + selector ----
  // 🎲 sentinel entry: resolves to a random mode-compatible map at join time,
  // so it is playable in every mode. Its `modes` comes from MODE_IDS rather
  // than a hand-written list — that list used to go stale every time a mode
  // was added (treasure and burnout were both missing at different points,
  // making 🎲 unpickable in them).
  const RANDOM_MAP = { id: "__random__", name: "🎲 Random Map", modes: MODE_IDS, theme: "day", spawns: [], vehicles: [], jumpPads: [], boxes: [] } as unknown as (typeof MAPS)[string];
  const maps = [...Object.values(MAPS), RANDOM_MAP];
  const DEFAULT_MENU_MAP_ID = "neo_island";
  let mapIdx = Math.max(0, maps.findIndex((m) => m.id === DEFAULT_MENU_MAP_ID));

  // remember the last mode/map/AI picks so you don't re-set them every visit
  const MENU_KEY = "neonpurge.menu";
  interface MenuPrefs {
    mode?: string; mapId?: string; difficulty?: string; bots?: string;
    purgeTeam?: boolean; vehicles?: boolean; aiOn?: boolean; ladder?: string;
    noTimer?: boolean; roundS?: string; monsters?: boolean; night?: boolean; soloTeams?: boolean; noAim?: boolean;
    pickupDemote?: boolean;
    soloTeamPicks?: number[];
    modifier?: string;
  }
  let prefs: MenuPrefs = {};
  try { prefs = JSON.parse(localStorage.getItem(MENU_KEY) ?? "{}"); } catch { prefs = {}; }
  const saveMenuPrefs = (): void => {
    try {
      localStorage.setItem(MENU_KEY, JSON.stringify({
        mode, mapId: maps[mapIdx]?.id, difficulty, bots: botInput.value,
        purgeTeam: purgeTeamCheck.checked, vehicles: vehiclesCheck.checked,
        aiOn, ladder: ladderSelect.value, roundS: roundSel.value, noAim: noAimCheck.checked, pickupDemote: pickupDemoteCheck.checked,
        monsters: monstersCheck.checked, night: nightCheck.checked, soloTeams: soloTeamsCheck.checked,
        soloTeamPicks: [...soloTeams],
        modifier: menuModifier,
      } satisfies MenuPrefs));
    } catch { /* storage disabled — fine */ }
  };
  const mapCanvas = document.createElement("canvas");
  mapCanvas.className = "map3d";
  const mapName = el("div", { class: "map3d-name" });
  const mapMeta = el("div", { class: "map3d-meta" });
  // desktop-only left "damier" quick-picker (created below); the arrow picker
  // and 3D preview stay — this just drives the same setMap.
  let damier: MapGridHandle | null = null;
  // MATCH-ASSET PREFETCH (game/matchPrefetch.ts): once the pick has settled,
  // start downloading the realistic models this map + mode will need, so the
  // match load screen finds them parsed instead of waiting on the network.
  // Debounced (arrow-cycling through maps must not fetch every one) and lazy-
  // imported (the model modules live in the match chunk, not the menu's).
  let prefetchTimer = 0;
  const schedulePrefetch = (): void => {
    window.clearTimeout(prefetchTimer);
    prefetchTimer = window.setTimeout(() => {
      const id = maps[mapIdx]?.id;
      if (!id || id === "__random__" || !screen.isConnected) return;
      const m = mode;
      void import("../game/matchPrefetch.js").then((p) => p.prefetchMatchAssets(id, m)).catch(() => { /* best-effort */ });
    }, 900);
  };
  const setMap = (i: number, announce = false): void => {
    mapIdx = (i + maps.length) % maps.length;
    const m = maps[mapIdx];
    damier?.setActive(m.id);
    mapName.textContent = m.name;
    if (announce) audio.announceMap(m.id, m.name); // recorded line, TTS fallback
    if (m.id === "__random__") {
      mapMeta.textContent = "spins the wheel — any map that fits the mode";
      preview?.showRandom(); // a rotating "?" stands in for the mystery map
      saveMenuPrefs();
      return;
    }
    const karts = (m.vehicles ?? []).filter((v) => v.kind === "kart").length;
    const helis = (m.vehicles ?? []).filter((v) => v.kind === "heli").length;
    mapMeta.textContent = `${m.theme === "day" ? "☀ Day" : "◈ Neon"}  ·  ${m.spawns.length} spawns  ·  🚗${karts} 🚁${helis}  ·  ${(m.jumpPads ?? []).length} pads`;
    preview?.loadMap(m);
    saveMenuPrefs();
    schedulePrefetch();
  };
  const prevBtn = el("button", { class: "ghost nav", text: "‹", title: "Previous map", "aria-label": "Previous map" });
  const nextBtn = el("button", { class: "ghost nav", text: "›", title: "Next map", "aria-label": "Next map" });
  prevBtn.dataset.cyclePrev = ""; nextBtn.dataset.cycleNext = ""; // d-pad ←/→ targets
  /** Indices of the maps playable in `m`, always including the 🎲 sentinel.
   *  This screen used to constrain the arrows for `arena` only, so you could
   *  cycle to a gungame-only map and start treasure on it — while the room
   *  lobby's own `cycle()` filtered correctly for every mode. The two screens
   *  now apply the same rule. Falls back to the full list if a mode somehow
   *  has no maps, so the arrows can never dead-end. */
  const mapIdxsFor = (m: string): number[] => {
    const idxs = maps.map((mm, i) => (mm.modes.includes(m) ? i : -1)).filter((i) => i >= 0);
    return idxs.length ? idxs : maps.map((_, i) => i);
  };
  const stepMap = (dir: number): void => {
    const pool = mapIdxsFor(mode);
    const pos = pool.indexOf(mapIdx);
    setMap(pool[((pos < 0 ? 0 : pos) + dir + pool.length) % pool.length], true);
  };
  prevBtn.onclick = () => stepMap(-1);
  nextBtn.onclick = () => stepMap(1);
  const mapInfoFocus = el("div", { class: "map3d-info" }, mapName, mapMeta);
  const mapCard = el("div", { class: "card map-card" },
    el("div", { class: "card-title", text: "MAP" }),
    el("div", { class: "map3d-wrap" }, mapCanvas, el("div", { class: "map3d-hint", text: "drag to rotate" })),
    el("div", { class: "map3d-bar", "data-pad-cycle-scope": "" }, prevBtn, mapInfoFocus, nextBtn),
  );

  // ---- MODE card ----
  let mode = "gungame";
  const modeBtns = MODE_ORDER.map(([id, label]) => {
    // no tooltip: the description line under the row is the ONE place the
    // mode is explained (a title repeating it was the same text twice)
    const b = el("button", { class: `seg${id === mode ? " active" : ""}`, text: label });
    b.onclick = () => setMode(id);
    return b;
  });
  // PURGE sub-option: team survival (revives) vs solo (last one alive wins)
  const purgeTeamCheck = el("input", { type: "checkbox" }) as HTMLInputElement;
  purgeTeamCheck.checked = true;
  purgeTeamCheck.onchange = () => { audio.voice(purgeTeamCheck.checked ? "teamgame" : "sologame", { interrupt: true }); saveMenuPrefs(); };
  const purgeRow = el("div", { class: "sub", style: "display:none" },
    el("label", {}, purgeTeamCheck, el("span", { text: " Team survival (mash △/F to revive) — off = last one alive wins" })));
  const ladderSelect = el("select", {});
  for (const l of Object.values(LADDERS)) ladderSelect.append(el("option", { value: l.id, text: l.displayName }));
  ladderSelect.onchange = () => saveMenuPrefs();
  const ladderRow = el("div", { class: "sub" }, el("label", { text: "Weapon ladder" }), ladderSelect);
  const modeDescEl = el("div", { class: "hint", style: "margin-top:0.35rem;line-height:1.35" });
  const setMode = (m: string, silent = false): void => {
    if (m !== mode && !silent) audio.voice(modeVoice(m), { interrupt: true }); // announcer names the mode
    const prevMode = mode; // captured BEFORE the assignment — the restore branch below needs it
    mode = m;
    MODE_ORDER.forEach(([id], i) => modeBtns[i].classList.toggle("active", id === m));
    modeDescEl.textContent = modeDesc(m, pickupDemoteCheck.checked);
    pickupDemoteRow.style.display = hasPickupDemoteOption(m) ? "" : "none";
    purgeRow.style.display = m === "purge" ? "block" : "none";
    // BURNOUT climbs the same ladder (on a clock), so the ladder picker matters
    // there too — Sprint/Duel make it a much shorter fuse. ONE GUN's gun
    // climbs it as well, so the picker sets that match's length too.
    // DESCENT reverses the CHOSEN ladder's gun rungs, so the picker matters
    // there too — hiding it stranded descent on the default ladder.
    ladderRow.style.display = m === "gungame" || m === "descent" || m === "burnout" || m === "onegun" ? "block" : "none"; // royale has no ladder (v2 rules)
    // mirror the LOBBY's per-mode rules: purge/arena strip vehicles; teams
    // exist only in gungame/ffa (purge has its own Team-survival row)
    menuVehiclesRow.style.display = m === "purge" || m === "arena" || m === "horde" ? "none" : ""; // horde strips vehicles (mapForMode) — a rider can't be clawed
    roundRow.style.display = m === "purge" ? "none" : "";
    // ARENA plays MKDD Bob-omb Blast rules: NO time limit — first to 3 points
    // (2 karts) / 4 (3+) wins (arenaWinTarget). The clock stays selectable as
    // an owner-chosen backstop; entering arena defaults it to "No timer" (0),
    // leaving arena restores the standard 10 min if still at that default.
    // (Create Room reads this same selector — it used to have a second,
    // numeric "Round time (s)" box of its own that had to be kept in sync.)
    if (m === "arena" && prevMode !== "arena") roundSel.value = String(ARENA_DEFAULT_ROUND_TIME_S);
    else if (m !== "arena" && prevMode === "arena" && roundSel.value === String(ARENA_DEFAULT_ROUND_TIME_S)) roundSel.value = "600";
    soloTeamsRow.style.display = m === "purge" || m === "arena" || m === "horde" ? "none" : ""; // horde owns its team model (zombies=2, humans=0)
    // Flags cannot exist without red/blue teams. Reflect that rule in the
    // normal menu too (including solo/AI), instead of showing an off switch
    // which the authoritative server will ignore later. Selecting Flags also
    // auto-spreads the roster; LEAVING Flags auto-unticks teams (both mirror
    // the room lobby, 2026-07-23 — hand-picked seats survive either way).
    if (m === "plunder") { soloTeamsCheck.checked = true; spreadSoloTeams(); saveMenuPrefs(); }
    else if (prevMode === "plunder" && soloTeamsCheck.checked) { soloTeamsCheck.checked = false; saveMenuPrefs(); }
    soloTeamsCheck.disabled = m === "plunder";
    renderTeamPicks();
    // Every mode is a playlist of the maps that declare it, so switching mode
    // off an incompatible map moves you onto a valid one (this used to happen
    // for arena and purge only). Arena prefers the SMALL purpose-built Block
    // City — the big maps like Rooftops XL lag on modest machines — but all
    // of a mode's maps stay pickable with the arrows.
    if (!maps[mapIdx].modes.includes(m)) {
      const pool = mapIdxsFor(m);
      const preferred = m === "arena" ? maps.findIndex((mm) => mm.id === "chibre_arena") : -1;
      setMap(pool.includes(preferred) ? preferred : pool[0]);
    }
    damier?.setMode(m); // re-filter the damier squares for the new mode
    schedulePrefetch(); // the zombie model depends on the mode, not the map
    saveMenuPrefs();
  };
  // match options (declared BEFORE the mode card that embeds them)
  const vehiclesCheck = el("input", { type: "checkbox", style: "width:auto" }) as HTMLInputElement;
  vehiclesCheck.checked = true;
  vehiclesCheck.onchange = () => saveMenuPrefs();
  const roundSel = el("select", {}) as HTMLSelectElement;
  fillRoundSelect(roundSel);
  roundSel.onchange = () => saveMenuPrefs();
  const roundRow = el("div", { class: "sub" }, el("label", { text: "Round time" }), roundSel);
  const monstersCheck = el("input", { type: "checkbox", style: "width:auto" }) as HTMLInputElement;
  monstersCheck.checked = true;
  monstersCheck.onchange = () => saveMenuPrefs();
  const nightCheck = el("input", { type: "checkbox", style: "width:auto" }) as HTMLInputElement;
  nightCheck.checked = true; // day/night cycle defaults ON
  nightCheck.onchange = () => saveMenuPrefs();
  const noAimCheck = el("input", { type: "checkbox", style: "width:auto" }) as HTMLInputElement;
  noAimCheck.onchange = () => saveMenuPrefs();
  // GUN GAME / DESCENT: power-up & vehicle kills demote the victim (default OFF)
  const pickupDemoteCheck = el("input", { type: "checkbox", style: "width:auto" }) as HTMLInputElement;
  pickupDemoteCheck.onchange = () => { modeDescEl.textContent = modeDesc(mode, pickupDemoteCheck.checked); saveMenuPrefs(); };
  const pickupDemoteRow = el("label", { class: "check-line", style: "margin-top:0.8rem" }, pickupDemoteCheck, el("span", { text: PICKUP_DEMOTE_LABEL }));
  const menuVehiclesRow = el("label", { class: "check-line" }, vehiclesCheck, el("span", { text: "Vehicles (karts + helicopters)" }));
  // ROUND MODIFIER — the solo/create paths have no lobby, so the mutator has to
  // be pickable here too or it would be host-only. Same shared MODIFIERS table.
  let menuModifier = "";
  const MENU_MODS: { id: string; label: string; desc: string; voice: string }[] = [
    { id: "", label: "Standard", desc: "No modifier — the mode's normal rules.", voice: "standard" },
    ...MODIFIERS.map((m) => ({ id: m.id, label: m.label, desc: m.desc, voice: m.voice })),
  ];
  const menuModDescEl = el("div", { class: "hint", style: "margin-top:0.35rem;line-height:1.35" });
  const menuModBtns = MENU_MODS.map((m) => {
    const b = el("button", { class: "seg", text: m.label });
    b.onclick = () => {
      if (m.voice && m.id !== menuModifier) audio.voice(m.voice, { interrupt: true }); // announcer names the modifier
      setMenuModifier(m.id);
      saveMenuPrefs();
    };
    return b;
  });
  const setMenuModifier = (id: string): void => {
    menuModifier = MENU_MODS.some((m) => m.id === id) ? id : "";
    MENU_MODS.forEach((m, i) => menuModBtns[i].classList.toggle("active", m.id === menuModifier));
    menuModDescEl.textContent = (MENU_MODS.find((m) => m.id === menuModifier) ?? MENU_MODS[0]).desc;
  };
  setMenuModifier("");
  const menuModRow = el("div", { class: "sub", style: "margin-top:0.8rem" },
    el("label", { text: "Round modifier" }),
    el("div", { class: "seg-row" }, ...menuModBtns),
    menuModDescEl);
  const matchOptsRow = el("div", { class: "sub" },
    roundRow,
    menuVehiclesRow,
    el("label", { class: "check-line", style: "margin-top:0.8rem" }, monstersCheck, el("span", { text: "Special map SFX (giant monsters)" })),
    el("label", { class: "check-line", style: "margin-top:0.8rem" }, nightCheck, el("span", { text: "Day/night cycle" })),
    el("label", { class: "check-line", style: "margin-top:0.8rem" }, noAimCheck, el("span", { text: "Disable aim assist (force off)" })),
    pickupDemoteRow,
    menuModRow);
  const modeCard = el("div", { class: "card" },
    el("div", { class: "card-title", text: "MODE" }),
    el("div", { class: "seg-row", "data-mode-cycle-scope": "" }, ...modeBtns),
    modeDescEl,
    purgeRow,
    ladderRow,
    fold("⚙ MATCH OPTIONS", matchOptsRow),
  );

  // ---- AI card ----
  let aiOn = true;
  let difficulty = "veryeasy"; // default for a fresh device (persisted once changed)
  const aiSwitch = el("button", { class: "switch on", text: "ON" });
  const botInput = el("input", { type: "range", min: "1", max: "9", value: "1", class: "slider" }) as HTMLInputElement;
  const botLabel = el("span", { class: "bot-count", text: "1 bot" });
  botInput.oninput = () => {
    botLabel.textContent = `${botInput.value} bot${botInput.value === "1" ? "" : "s"}`;
    audio.voiceBots(Number(botInput.value)); // spoken "<n> bot(s)", like the room lobby
    if (soloTeamsCheck.checked) spreadSoloTeams(); // new AI slots appear pre-assigned
    renderTeamPicks();
    saveMenuPrefs();
  };
  const DIFFS = DIFFICULTIES;
  const diffBtns = DIFFS.map((d) =>
    el("button", { class: `seg${d.id === difficulty ? " active" : ""}`, text: d.label }));
  diffBtns.forEach((b, i) => (b.onclick = () => {
    difficulty = DIFFS[i].id;
    audio.voice(difficulty); // all four have recorded announcer lines now
    diffBtns.forEach((x, j) => x.classList.toggle("active", i === j));
    saveMenuPrefs();
  }));
  const soloTeamsCheck = el("input", { type: "checkbox", style: "width:auto" }) as HTMLInputElement;
  // per-slot team pickers for solo (no lobby phase to assign them in):
  // index 0 = YOU, 1..n = AI slots. 0 = auto-balance, 1 = red, 2 = blue.
  const soloTeams: number[] = [];
  // mirror the room lobby's auto-spread (2026-07-23): fill every BLANK seat
  // (You + each AI) evenly red/blue so enabling teams / picking Flags shows a
  // full, even roster immediately. Hand-picked seats are left alone, so edits
  // persist (saveMenuPrefs stores the concrete values).
  const spreadSoloTeams = (): void => {
    const n = Number(botInput.value) || 1;
    soloTeams.length = n + 1;
    let red = 0, blue = 0;
    for (const t of soloTeams) { if (t === 1) red++; else if (t === 2) blue++; }
    for (let i = 0; i <= n; i++) {
      if (soloTeams[i] === 1 || soloTeams[i] === 2) continue;
      soloTeams[i] = red <= blue ? 1 : 2;
      if (soloTeams[i] === 1) red++; else blue++;
    }
  };
  const teamPickEl = el("div", { class: "sub" });
  const renderTeamPicks = (): void => {
    if (!soloTeamsCheck.checked) { teamPickEl.replaceChildren(); return; }
    const n = Number(botInput.value) || 1;
    soloTeams.length = n + 1;
    const rows: HTMLElement[] = [];
    for (let i = 0; i <= n; i++) {
      const cur = soloTeams[i] ?? 0;
      const name = el("span", {
        text: i === 0 ? "You" : `AI ${i}`,
        style: `min-width:4.5em;display:inline-block;color:${teamColor(cur)}`,
      });
      const mk = (t: number, label: string, color: string): HTMLElement => {
        const b = el("button", { class: "ghost", text: cur === t ? `[${label}]` : label, style: `color:${color};padding:0 0.5em` });
        b.onclick = () => { soloTeams[i] = cur === t ? 0 : t; renderTeamPicks(); saveMenuPrefs(); };
        return b;
      };
      rows.push(el("div", { class: "row", style: "align-items:center;gap:0.4rem" }, name, mk(1, "RED", TEAM_COLOR.red), mk(2, "BLUE", TEAM_COLOR.blue)));
    }
    teamPickEl.replaceChildren(el("label", { text: "Team assignment (blank = auto)" }), ...rows);
  };
  soloTeamsCheck.onchange = () => {
    audio.voice(soloTeamsCheck.checked ? "teamgame" : "sologame", { interrupt: true });
    if (soloTeamsCheck.checked) spreadSoloTeams(); // show a full even roster, not blanks
    renderTeamPicks();
    saveMenuPrefs();
  };
  const soloTeamsRow = el("label", { class: "check-line", style: "margin-top:0.8rem" }, soloTeamsCheck, el("span", { text: "Teams (you + AI vs AI, no friendly fire)" }));
  // AI card = ONLY bot things (count, difficulty, bot teams); the general
  // match options (timer / monsters / night) live in the MODE card
  const botRow = el("div", { class: "sub" },
    el("label", { text: "Bots" }), el("div", { class: "row", style: "align-items:center;gap:0.8rem" }, botInput, botLabel),
    el("label", { text: "Difficulty", style: "margin-top:0.8rem" }), el("div", { class: "seg-row" }, ...diffBtns),
    soloTeamsRow,
    teamPickEl);
  aiSwitch.onclick = () => {
    aiOn = !aiOn;
    aiSwitch.classList.toggle("on", aiOn);
    aiSwitch.textContent = aiOn ? "ON" : "OFF";
    botRow.style.display = aiOn ? "block" : "none";
    playBtn.textContent = aiOn ? "PLAY VS AI" : "PLAY ONLINE";
    // with AI off the primary button IS quick play — don't show a second
    // button that does exactly the same thing
    quickBtn.style.display = aiOn ? "" : "none";
    saveMenuPrefs();
  };
  const aiCard = el("div", { class: "card" },
    el("div", { class: "card-title", text: "AI OPPONENTS" }),
    el("div", { class: "switch-row" }, el("span", { text: "Add AI bots" }), aiSwitch),
    botRow,
  );

  // ---- primary actions ----
  const resolveMapId = (): string => {
    const m = maps[mapIdx];
    if (m.id !== "__random__") return m.id;
    const pool = maps.filter((mm) => mm.id !== "__random__" && mm.modes.includes(mode));
    return pool[Math.floor(Math.random() * pool.length)]?.id ?? maps[0].id;
  };
  const playBtn = el("button", { class: "primary wide big", text: "PLAY VS AI", "data-pad-start": "" });
  playBtn.onclick = () => {
    audio.voice("gamestart", { interrupt: true }); // announcer: match incoming
    padRumble(2000, 1, 1); // VIOLENT launch rumble
    const m = { id: resolveMapId() };
    if (aiOn) {
      join(createSoloRoom({ mapId: m.id, ladderId: ladderSelect.value, mode, purgeTeam: purgeTeamCheck.checked, bots: Number(botInput.value) || 1, difficulty, roundTimeS: Number(roundSel.value), monsters: monstersCheck.checked, nightCycle: nightCheck.checked, vehicles: vehiclesCheck.checked, noAutoAim: noAimCheck.checked, pickupVehicleDemote: pickupDemoteCheck.checked, modifier: menuModifier, teams: mode === "plunder" || soloTeamsCheck.checked, myTeam: soloTeams[0] || 0, botTeams: soloTeams.slice(1).map((t) => t || 0) }));
    } else {
      join(joinQuickPlay({ mapId: resolveMapId(), mode, purgeTeam: purgeTeamCheck.checked, modifier: menuModifier }));
    }
  };
  const quickBtn = el("button", { class: "wide", text: "Quick Play (online)", title: "Jump into an online match with other players" });
  quickBtn.onclick = () => { audio.voice("gamestart", { interrupt: true }); padRumble(2000, 1, 1); join(joinQuickPlay({ mapId: resolveMapId(), mode, purgeTeam: purgeTeamCheck.checked, modifier: menuModifier })); };

  // ---- PLAY WITH FRIENDS (2026-11 UX pass) ----
  // Everything that puts you in a room with other people sits in ONE group
  // right under the primary button: host, join by code, the public room, and
  // couch split-screen. It used to be split between the action column (public,
  // local) and a ROOMS card that was a collapsed drawer on phones, so the
  // most common social path (create a room, invite) was the hardest to find.

  // THE public hangout — always there, first one in owns it, come and go
  const publicBtn = el("button", { class: "magenta", text: "⚡ Public Room", title: "The always-open public lobby: anyone can drop in" });
  publicBtn.onclick = () => join(joinPublicRoom());

  // MULTIPLAYER LOCAL: 2-4 players split-screen on THIS machine (own lobby)
  const localBtn = el("button", { text: "🎮 Split-screen", title: "MULTIPLAYER LOCAL: 2-4 players with controllers on this screen" });
  localBtn.onclick = () => { cleanup(); actions.onLocal(); };

  const capInput = el("input", { type: "number", value: "16", min: "2", max: String(MAX_ROOM_CAP), "aria-label": "Max players" });
  const privateCheck = el("input", { type: "checkbox", style: "width:auto" }) as HTMLInputElement;
  const createBtn = el("button", { class: "magenta wide", text: "＋ Create Room", title: "Host a room with the mode and map picked here, then invite friends from the room lobby" });
  createBtn.onclick = () => join(createRoom({
    mapId: maps[mapIdx].id, ladderId: ladderSelect.value, mode,
    // round time comes from the ONE round-time picker in MATCH OPTIONS (this
    // card used to carry a second, numeric "Round time (s)" box)
    maxPlayers: Number(capInput.value) || 16, roundTimeS: Number(roundSel.value),
    isPrivate: privateCheck.checked,
    bots: aiOn ? Number(botInput.value) || 0 : 0, difficulty, // menu bot count carries into the room
    vehicles: vehiclesCheck.checked,
    teams: mode === "plunder" ? true : undefined,
    // teams is deliberately not sent — the room LOBBY owns that toggle (the
    // tick was removed from this card, leaving a detached checkbox that only
    // ever reported false)
    monsters: monstersCheck.checked,
    nightCycle: nightCheck.checked,
    noAutoAim: noAimCheck.checked,
    pickupVehicleDemote: pickupDemoteCheck.checked,
    modifier: menuModifier,
  }));

  const codeInput = el("input", { type: "text", placeholder: "ROOM CODE", maxlength: "6", "aria-label": "Room code", autocomplete: "off", style: "text-transform:uppercase;letter-spacing:0.4em;text-align:center" }) as HTMLInputElement;
  const joinCodeBtn = el("button", { text: "Join", title: "Join the room with this code" });
  joinCodeBtn.onclick = () => {
    const code = codeInput.value.trim().toUpperCase();
    if (code.length < 4) { errorEl.textContent = "Enter the 6-letter room code your friend sees at the top of their lobby."; codeInput.focus(); return; }
    join(joinByCode(code));
  };
  // Enter in the code box joins (keyboard users shouldn't have to tab over)
  codeInput.addEventListener("keydown", (e) => { if (e.key === "Enter") { e.preventDefault(); joinCodeBtn.click(); } });

  const friendsGroup = el("div", { class: "card play-friends" },
    el("div", { class: "card-title", text: "PLAY WITH FRIENDS" }),
    createBtn,
    fold("⚙ ROOM OPTIONS",
      el("div", { class: "row play-roomopts" },
        el("label", { class: "inline-field" }, el("span", { text: "Max players" }), capInput),
        el("label", { class: "check-line" }, privateCheck, el("span", { text: "Private (code only)" })))),
    el("div", { class: "row play-joincode" }, codeInput, joinCodeBtn),
    el("div", { class: "row play-friends-more" }, publicBtn, localBtn),
  );

  // ---- OPEN ROOMS card: browse rooms other people are hosting ----
  const listEl = el("div", { class: "room-list", "aria-live": "polite" });
  const refreshBtn = el("button", { class: "ghost", text: "↻ Refresh", title: "Reload the room list" });
  const refresh = async (): Promise<void> => {
    refreshBtn.disabled = true;
    refreshBtn.textContent = "↻ Loading…";
    try {
      const rooms = await listRooms();
      // live count on the public button ("3 in" beats an empty promise); the
      // public hub is NOT repeated as a row below — the button is its one home
      const pub = rooms.find((r) => (r.metadata as { hub?: string } | undefined)?.hub === "public");
      publicBtn.textContent = pub ? `⚡ Public Room (${pub.clients} in)` : "⚡ Public Room";
      const others = rooms.filter((r) => r !== pub);
      listEl.replaceChildren(...(others.length === 0
        ? [el("div", { class: "hint", text: "No open rooms right now. Create one above and invite friends." })]
        : others.map((r) => {
            const meta = (r.metadata ?? {}) as { code?: string; mapId?: string; hub?: string };
            const full = r.clients >= r.maxClients;
            const row = el("div", { class: "room-row" },
              el("span", { class: "room-code", text: meta.code ?? "??????" }),
              el("span", { text: MAPS[meta.mapId ?? ""]?.name ?? "Custom map" }),
              el("span", { text: `${r.clients}/${r.maxClients}`, title: "players / max" }));
            const b = el("button", { text: full ? "Full" : "Join", title: full ? "This room is full" : `Join room ${meta.code ?? ""}` });
            b.disabled = full;
            b.onclick = () => join(joinById(r.roomId));
            row.append(b);
            return row;
          })));
    } catch (err) { listEl.replaceChildren(el("div", { class: "error", text: (err as Error).message })); }
    finally { refreshBtn.disabled = false; refreshBtn.textContent = "↻ Refresh"; }
  };
  refreshBtn.onclick = () => void refresh();
  void refresh();

  const roomsCard = el("div", { class: "card" },
    el("div", { class: "row-between" }, el("div", { class: "card-title", text: "OPEN ROOMS" }), refreshBtn),
    listEl,
  );

  const backBtn = el("button", { class: "ghost wide mt1", text: "‹ Back to Menu" });
  backBtn.onclick = () => { cleanup(); actions.onBack(); };

  // feedback for every click lands HERE, next to the buttons that cause it —
  // it used to be appended below the whole page, off-screen when scrolled
  errorEl.setAttribute("role", "alert");
  const left = el("div", { class: "play-col" }, mapCard,
    el("div", { class: "actions" }, playBtn, quickBtn, errorEl), friendsGroup, backBtn);
  const right = el("div", { class: "play-col" }, modeCard, aiCard, roomsCard);

  // DESKTOP damier: a clickable map grid on the left, alongside the existing
  // arrow picker. Picking a square = same as cycling to it. Hidden on touch.
  // COMMUNITY MAPS browser (opened by the damier's 🎨 Custom tile): pick a
  // published map → play it SOLO vs bots (the map data is passed to the server).
  const openCustomPanel = (): void => {
    const overlay = el("div", { class: "screen overlay overlay-lite" });
    const list = el("div", { class: "editor-mine" });
    const closeBtn = el("button", { class: "ghost wide mt1", text: "Close" });
    closeBtn.onclick = () => overlay.remove();
    overlay.append(el("div", { class: "panel", style: "max-width:480px" },
      el("h2", { text: "🎨 Community Maps" }),
      el("div", { class: "hint", text: "Published by players — pick one to play vs AI." }),
      list, closeBtn));
    screen.append(overlay);
    list.replaceChildren(el("div", { class: "hint", text: "Loading…" }));
    api.listPublishedMaps().then((pub) => {
      list.replaceChildren(...(pub.length ? pub.map((mm) => {
        const playB = el("button", { class: "seg", text: "▶ Solo" });
        playB.onclick = async () => {
          try {
            const full = await api.getMap(mm.id);
            audio.voice("gamestart", { interrupt: true }); padRumble(2000, 1, 1);
            overlay.remove();
            join(createSoloRoom({ customMap: full.data, mode, bots: Number(botInput.value) || 4, difficulty, roundTimeS: Number(roundSel.value), monsters: monstersCheck.checked, nightCycle: nightCheck.checked, vehicles: vehiclesCheck.checked, noAutoAim: noAimCheck.checked, pickupVehicleDemote: pickupDemoteCheck.checked, modifier: menuModifier }));
          } catch (e) { errorEl.textContent = (e as Error).message; }
        };
        const hostB = el("button", { class: "seg", text: "🌐 Host" });
        hostB.onclick = async () => {
          try {
            const full = await api.getMap(mm.id);
            audio.voice("gamestart", { interrupt: true }); padRumble(2000, 1, 1);
            overlay.remove();
            join(createRoom({ customMap: full.data, mode, maxPlayers: 16, roundTimeS: Number(roundSel.value), bots: aiOn ? Number(botInput.value) || 0 : 0, difficulty, vehicles: vehiclesCheck.checked, monsters: monstersCheck.checked, nightCycle: nightCheck.checked, noAutoAim: noAimCheck.checked, pickupVehicleDemote: pickupDemoteCheck.checked, modifier: menuModifier }));
          } catch (e) { errorEl.textContent = (e as Error).message; }
        };
        return el("div", { class: "editor-mine-row" }, el("span", { text: `${mm.name}  ·  by ${mm.author}` }), el("div", {}, playB, hostB));
      }) : [el("div", { class: "hint", text: "No published maps yet — be the first with 🛠 Build a Map!" })]));
    }).catch((e) => list.replaceChildren(el("div", { class: "hint", text: (e as Error).message || "Failed to load." })));
  };
  if (!isTouchDevice()) {
    damier = mapGrid({
      mode,
      activeId: maps[mapIdx].id,
      onPick: (id) => {
        const i = maps.findIndex((m) => m.id === id);
        if (i >= 0) setMap(i, true);
      },
      onCustom: openCustomPanel,
    });
  }
  // has-damier: the grid is the one desktop map picker, so CSS hides the
  // preview arrows (still clicked by the pad d-pad) while the grid shows
  if (damier) screen.classList.add("has-damier");
  const playInner = damier
    ?el("div", { class: "play-outer" }, damier.root, el("div", { class: "play-grid" }, left, right))
    : el("div", { class: "play-grid" }, left, right);

  screen.append(
    el("div", { class: "play-head" },
      el("div", { class: "brand small", text: "NEON PURGE" }),
      el("div", { class: "brand-sub", text: "FIND A MATCH" })),
    playInner,
  );

  // restore BEFORE the first setMode so its description reads the saved tick
  if (prefs.pickupDemote !== undefined) pickupDemoteCheck.checked = prefs.pickupDemote;
  modeDescEl.textContent = modeDesc(mode, pickupDemoteCheck.checked);
  // restore last session's picks (silent — no mode voice on menu open)
  if (prefs.mode) setMode(prefs.mode, true);
  if (prefs.difficulty) {
    const di = DIFFS.findIndex((d) => d.id === prefs.difficulty);
    if (di >= 0) { difficulty = prefs.difficulty; diffBtns.forEach((x, j) => x.classList.toggle("active", j === di)); }
  }
  if (prefs.bots) { botInput.value = prefs.bots; botLabel.textContent = `${prefs.bots} bot${prefs.bots === "1" ? "" : "s"}`; }
  if (prefs.purgeTeam !== undefined) purgeTeamCheck.checked = prefs.purgeTeam;
  if (prefs.aiOn === false) aiSwitch.click(); // replays the full OFF side-effects
  if (prefs.ladder && [...ladderSelect.options].some((o) => o.value === prefs.ladder)) ladderSelect.value = prefs.ladder;
  if (prefs.roundS && [...roundSel.options].some((o) => o.value === prefs.roundS)) roundSel.value = prefs.roundS;
  else if (prefs.noTimer && mode !== "arena") roundSel.value = "0"; // migrate the old checkbox pref
  if (prefs.vehicles !== undefined) vehiclesCheck.checked = prefs.vehicles;
  if (prefs.noAim !== undefined) noAimCheck.checked = prefs.noAim;
  if (prefs.modifier !== undefined) setMenuModifier(prefs.modifier);
  if (prefs.monsters !== undefined) monstersCheck.checked = prefs.monsters;
  if (prefs.night !== undefined) nightCheck.checked = prefs.night;
  if (prefs.soloTeamPicks) soloTeams.splice(0, soloTeams.length, ...prefs.soloTeamPicks.map((t) => t === 1 || t === 2 ? t : 0));
  if (prefs.soloTeams !== undefined && mode !== "plunder") soloTeamsCheck.checked = prefs.soloTeams;
  if (mode === "plunder") { soloTeamsCheck.checked = true; soloTeamsCheck.disabled = true; spreadSoloTeams(); }
  renderTeamPicks();

  // init the 3D preview once the canvas is laid out in the DOM
  requestAnimationFrame(() => {
    if (!screen.isConnected) return;
    preview = createMapPreview(mapCanvas);
    // restore the last map, but only if it still fits the restored mode —
    // otherwise keep the valid one setMode already picked
    const savedIdx = prefs.mapId ? maps.findIndex((m) => m.id === prefs.mapId) : -1;
    setMap(mapIdxsFor(mode).includes(savedIdx) ? savedIdx : mapIdx);
    preview.start();
  });

  return screen;
}
